/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { UsergroupAddOutlined } from '@ant-design/icons'
import {
  canSubmitRosterChoice,
  type RosterChoice,
} from '@sportninja/common/features/competition/operations/utils/addTeamPayload'
import type { OrgSearchTeam } from '@sportninja/common/services/team/orgTeamSearchService'
import type { OperationsScheduleNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import { isTeamNode } from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import { paragraphSmall } from 'src/components/css'
import BaseModal from 'src/components/ts-components/BaseModal'
import Button from 'src/components/ts-components/Button'
import { useOrgTeamSearch } from '../../hooks/useOrgTeamSearch'
import { useTeamRosterChoices } from '../../hooks/useTeamRosterChoices'
import { useAddTeamToDivision } from '../../hooks/useAddTeamToDivision'
import RosterChoiceStep from './RosterChoiceStep'
import TeamSearchStep from './TeamSearchStep'

const translate = t as (key: string, options?: unknown) => string

interface AddTeamModalProps {
  /** The division the team is added to. */
  division: OperationsScheduleNode
  orgId: string
  /** Root competition — search scope and existing-roster match. */
  rootScheduleId: string
  sportId: string | null
  /** Generic-sport competitions filter the search by this instead of sportId. */
  genericSportId?: string | null
  onClose: () => void
  onAdded: () => void
}

/**
 * Add a team to a division: search-or-create, then choose a roster.
 *
 * Restores the legacy Setup tab's two-part flow, which the first cut of this
 * tab dropped in favour of a create-only `TeamModal`. Both paths POST to the
 * same `/schedules/:divisionId/teams`; the roster step appears only for an
 * existing team, because creating one by name makes its roster server-side.
 */
const AddTeamModal = ({
  division,
  orgId,
  rootScheduleId,
  sportId,
  genericSportId = null,
  onClose,
  onAdded,
}: AddTeamModalProps) => {
  'use memo'

  const [query, setQuery] = useState('')
  const [isGlobal, setIsGlobal] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState<OrgSearchTeam | null>(null)
  const [isNewTeamMode, setIsNewTeamMode] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [rosterChoice, setRosterChoice] = useState<RosterChoice>(null)

  const search = useOrgTeamSearch({
    orgId,
    rootScheduleId,
    query,
    isGlobal,
    sportId,
    genericSportId,
    enabled: !isNewTeamMode,
  })

  const rosters = useTeamRosterChoices({
    teamId: selectedTeam?.id ?? null,
    rootScheduleId,
    enabled: Boolean(selectedTeam),
  })

  const { addExisting, createNew, isSaving, error } = useAddTeamToDivision({
    divisionScheduleId: division.scheduleId,
    onAdded,
  })

  // Teams already under this division are still returned by the search, so they
  // are marked rather than silently missing — matching them here avoids a
  // confusing "nothing happened" click.
  const existingTeamIds = new Set(
    division.children.filter(isTeamNode).map((team) => team.teamId)
  )

  const isRosterStep = Boolean(selectedTeam)

  const canSubmit = isNewTeamMode
    ? newTeamName.trim().length > 0
    : isRosterStep &&
      canSubmitRosterChoice({
        isPlaceholder: selectedTeam?.isPlaceholder ?? false,
        hasExistingCompetitionRoster: Boolean(rosters.existing),
        rosterChoice,
      })

  const isSearchStep = !selectedTeam && !isNewTeamMode

  // Back always lands on the search, from either the roster step or the
  // new-team field.
  const handleBack = () => {
    setSelectedTeam(null)
    setRosterChoice(null)
    setIsNewTeamMode(false)
    setNewTeamName('')
  }

  const handleSubmit = async () => {
    if (isNewTeamMode) {
      await createNew(newTeamName)
      return
    }
    if (selectedTeam) {
      await addExisting(selectedTeam.id, rosterChoice)
    }
  }

  return (
    <BaseModal
      isOpen
      onClose={onClose}
      title={capitalizeWords(
        translate('Web:addTeamTo', { name: division.name })
      )}
      desktopWidth='672px'
      // The search step has nothing to confirm — picking a result advances on
      // its own — so it swaps the OK button for "New Team", matching the Add
      // Player modal's footer. Only the other two steps submit.
      actions={
        isSearchStep ? (
          <div css={footerStyle}>
            <div css={footerButtonStyle}>
              <Button
                text={translate('Web:newTeam')}
                onClick={() => setIsNewTeamMode(true)}
                variant='secondary'
                style={newTeamButtonStyle}
                icon={<UsergroupAddOutlined css={newTeamIconStyle} />}
              />
            </div>
            <div css={footerButtonStyle}>
              <Button
                text={translate('common:cancel')}
                onClick={onClose}
                variant='secondary'
              />
            </div>
          </div>
        ) : (
          <div css={footerEndStyle}>
            <div css={footerButtonStyle}>
              <Button
                text={translate('common:back')}
                onClick={handleBack}
                variant='secondary'
              />
            </div>
            <div css={footerButtonStyle}>
              <Button
                text={translate('Web:addTeam')}
                onClick={handleSubmit}
                variant='primary'
                disabled={!canSubmit || rosters.isLoading || isSaving}
              />
            </div>
          </div>
        )
      }
    >
      <div css={bodyStyle}>
        {isRosterStep && selectedTeam ? (
          <RosterChoiceStep
            teamName={selectedTeam.nameFull}
            choices={rosters.choices}
            existing={rosters.existing}
            isPlaceholder={selectedTeam.isPlaceholder}
            isLoading={rosters.isLoading}
            value={rosterChoice}
            onChange={setRosterChoice}
          />
        ) : (
          <TeamSearchStep
            query={query}
            onQueryChange={setQuery}
            isGlobal={isGlobal}
            onGlobalChange={setIsGlobal}
            teams={search.teams}
            isFetching={search.isFetching}
            // Picking a result IS the action — it advances straight to the
            // roster step, matching the Add Player modal's select behaviour.
            onSelectTeam={(team) => {
              setSelectedTeam(team)
              setRosterChoice(null)
            }}
            isNewTeamMode={isNewTeamMode}
            newTeamName={newTeamName}
            onNewTeamNameChange={setNewTeamName}
            existingTeamIds={existingTeamIds}
          />
        )}

        {error && <p css={errorStyle}>{error}</p>}
      </div>
    </BaseModal>
  )
}

const footerStyle = css`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`

const footerEndStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`

// Button has flex:1, so a sized wrapper keeps it from stretching full width.
const footerButtonStyle = css`
  display: flex;
  width: 160px;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const newTeamButtonStyle = css`
  border-color: ${colors.PRIMARY};
  white-space: nowrap;
`

const newTeamIconStyle = css`
  color: ${colors.WHITE};
  font-size: 20px;
`

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
  /* BaseModal's desktop body is overflow:hidden, so a long roster list must
     scroll INSIDE itself — see listStyle in RosterChoiceStep. Without
     min-height:0 here that flex child refuses to shrink, its overflow never
     engages, and the content is clipped with the footer pushed out of reach. */
  min-height: 0;
  flex: 1 1 auto;
`

const errorStyle = css`
  ${paragraphSmall}
  margin: 0;
  color: ${colors.ERROR_LIGHT};
`

export default AddTeamModal
